import React, { useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";

const dummy = new THREE.Object3D();

// A performant Lower-Manhattan skyline built entirely from InstancedMesh (each
// tier = one draw call) with a simple 2-tier LOD (near/far) — 90+ FPS on spatial
// devices. (The street-level crowd is now a small set of full-limb hero runners
// handled in ArrivalScene, so the scene focuses on the escape + aftermath.)

function useInstances(count, place) {
  return useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) arr.push(place(i));
    return arr;
  }, [count, place]);
}

function BuildingTier({ count, radiusMin, radiusMax, minH, maxH, color, seed }) {
  const ref = useRef();
  const data = useInstances(count, (i) => {
    const rnd = (Math.sin(seed + i * 12.9898) * 43758.5453) % 1;
    const r = (v) => Math.abs((Math.sin(seed + i * v) * 43758.5453) % 1);
    const ang = r(3.1) * Math.PI * 2;
    const rad = radiusMin + r(7.7) * (radiusMax - radiusMin);
    const x = Math.cos(ang) * rad;
    const z = -25 - Math.abs(Math.sin(ang)) * (radiusMax) - r(5.3) * 30;
    const h = minH + r(9.1) * (maxH - minH);
    const w = 4 + r(2.3) * 8;
    const d = 4 + r(4.6) * 8;
    return { x, z, h, w, d, tint: 0.75 + r(1.7) * 0.4, _u: rnd };
  });
  useLayoutEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const base = new THREE.Color(color);
    data.forEach((b, i) => {
      dummy.position.set(b.x, b.h / 2, b.z);
      dummy.scale.set(b.w, b.h, b.d);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, base.clone().multiplyScalar(b.tint));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [data, color]);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, count]} frustumCulled>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={color} roughness={0.85} metalness={0.05} />
    </instancedMesh>
  );
}

// ~1500 evacuees. Static ambient until the collapse roar builds, then ALL of them
// sprint down Liberty Street (+Z, away from the South Tower) with a running bob +
// forward lean, looping back into the depth so the exodus never thins out.
export default function InstancedCity() {
  return (
    <group>
      {/* LOD near tier — taller, closer silhouettes */}
      <BuildingTier count={300} radiusMin={40} radiusMax={95} minH={16} maxH={64} color={0x6a7078} seed={11.1} />
      {/* LOD far tier — smaller, hazier skyline */}
      <BuildingTier count={500} radiusMin={90} radiusMax={165} minH={10} maxH={40} color={0x565c64} seed={73.7} />
    </group>
  );
}
